/**
 * REWIND フィルタ - メタフィールド数値レンジスライダー
 * S&Dフィルタと同居。デスクトップはサイドバー内に永続表示、モバイルはドロワー内に表示。
 *
 * スライダーを動かすと「スペック表示」に切り替わる。
 *   1. Storefront API で、コレクション全体（形状・素材などの絞り込みと並び順を反映）の商品の並びを取得する
 *   2. その中から重量・直径・幅が範囲内の商品を割り出す
 *   3. 表示するページ分のカードを sections/rewind-product-cards.liquid から取得して差し込む
 *      （今のページに無い商品も出せる）
 * スライダーを初期位置に戻すと、サーバーが描画した通常の一覧に戻る。
 *
 * 「在庫切れ」だけで絞り込んだときも、スライダーを動かしていなくてもスペック表示にする。
 * Shopify の在庫の絞り込みはバリエーション単位で、1色でも切れていれば「在庫切れ」に入るため、
 * ここで「どのバリエーションも買えない商品」だけに絞り直す（_soldOutOnly を参照）。
 *
 * 並び順が重量・直径・幅のとき（sort_by=weight-ascending など。Shopify には無い並び順）も、スペック表示にする。
 * コレクション全体を、取得済みの数値で並べ替える（_specSort を参照）。
 *
 * テーマ側（theme.js の CollectionSection）は、絞り込み・並び順・表示数・ページを変えるたびに
 * .collection__dynamic-part を描き直して theme:loading:end を出す。そのたびにここでスペック表示を作り直す。
 */

const REWIND_API_VERSION       = '2026-07';
const REWIND_CARDS_SECTION     = 'rewind-product-cards';
const REWIND_CARDS_PER_REQUEST = 20; // all_products の上限（1回の描画で20商品）
const REWIND_CARDS_CONCURRENCY = 4;
const REWIND_APPLY_DELAY       = 250; // スライダー操作中に毎回取りに行かない

// 商品データ（localStorage）を使う上限。これより新しければ、まずそれでスライダーと一覧を作り、
// 裏で取り直して、変わっていれば描き直す（refreshInBackground）。取得を待つ間は既定の並びが出るので、待たせない。
// 取り直しが終わるまでの1〜2秒は、在庫・数値が古いことがある（カードの表示は常に最新）
const REWIND_PRODUCTS_CACHE_TTL = 24 * 60 * 60 * 1000;

// スライダーの目盛り。商品が集まっている帯（下位10%〜上位10%の間）に、トラックの中央70%を割り当てる。
// 両端の15%ずつは、付属品などの外れた値の分。_sliderRange を参照
const REWIND_SCALE_LOW_PERCENTILE  = 0.1;
const REWIND_SCALE_HIGH_PERCENTILE = 0.9;
const REWIND_SCALE_LOW_POSITION    = 15; // トラック上の位置（%）
const REWIND_SCALE_HIGH_POSITION   = 85;
const REWIND_SCALE_MIN_VALUES      = 20;  // これより少ないと分布が当てにならないので等間隔
const REWIND_SCALE_MIN_GAIN        = 1.2; // 帯が等間隔のときの1.2倍以上に広がらないなら等間隔のまま

// sort_by の値 → Storefront API の [sortKey, reverse]
const REWIND_SORT_KEYS = {
  'manual':             ['MANUAL', false],
  'best-selling':       ['BEST_SELLING', false],
  'title-ascending':    ['TITLE', false],
  'title-descending':   ['TITLE', true],
  'price-ascending':    ['PRICE', false],
  'price-descending':   ['PRICE', true],
  'created-ascending':  ['CREATED', false],
  'created-descending': ['CREATED', true],
};

// sort_by の値（Shopify には無い並び順。選択肢は main-collection.liquid が出す）→ [項目, 大きい順か]
const REWIND_SPEC_SORTS = {
  'weight-ascending':    ['weight', false],
  'weight-descending':   ['weight', true],
  'diameter-ascending':  ['diameter', false],
  'diameter-descending': ['diameter', true],
  'width-ascending':     ['width', false],
  'width-descending':    ['width', true],
};

// sort_by は URL の値なので、'constructor' などがプロトタイプの値に当たらないよう、自前のキーだけを見る
const rewindLookup = (map, key) => Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;

// スペック表示中に書き換える件数表示（main-collection.liquid）
const REWIND_COUNT_TARGETS = {
  '.collection__showing-count':                 'showingCount',
  '.collection__products-count-showing':        'showingCount',
  '.collection__products-count-total':          'productsCount',
  '.collection__mobile-active-filters-results': 'resultsCount',
};

class RewindFilter {
  constructor(config) {
    this.collectionHandle = config.collectionHandle;
    this.storefrontToken  = config.storefrontToken;
    this.storeDomain      = config.storeDomain;
    this.settings         = config.settings || {}; // main-collection.liquid の data-rewind-filter-config
    this.country          = this.settings.country || null;
    this.language         = (this.settings.language || config.locale || 'ja').toUpperCase().replace('-', '_');
    this.cacheKey         = `rewind_products_v3_${this.collectionHandle}_${this.country || ''}`;
    this.listCacheKey     = `rewind_list_${this.collectionHandle}_${this.country || ''}`;
    this.cacheTTL         = 300_000; // 5分。絞り込み + 並び順ごとの商品の並び（sessionStorage）用

    this.products        = [];
    this._loaded         = false;   // 商品データの取得が終わったか（失敗して空のまま、も含む）
    this.sliderRanges    = {}; // { weight: { min, max }, ... }
    this.sliderDataRanges = {}; // { weight: { min, max }, ... } データ全体の範囲
    this._syncing        = false;
    this._building       = false;

    this.listCache    = new Map(); // 絞り込み条件 + 並び順 → 商品ハンドルの並び（Promise）
    this.cardCache    = new Map(); // ハンドル → カードのHTML
    this.cardRequests = new Map(); // ハンドル → 取得中のリクエスト（Promise）
    this.page         = this._pageFromUrl();
    this._spec        = null;      // スペック表示中の要素と、退避したサーバー側の一覧
    this._renderedKey = null;
    this._listState   = null;      // 直前の絞り込み条件 + 並び順 + 表示数（変わったら1ページ目へ）
    this._fromHistory = false;     // 戻る/進むの直後（popstate で立てる）
    this._applySeq    = 0;
    this._applyTimer  = null;
    this._lastTotal   = null;      // スペック表示の該当件数（ドロワーのボタンに出す）
    this._pending     = false;
    this._pendingLabel = false;
    this._pendingTimer = null;

    // 以前のキー名（国別になる前・available を持つ前）のキャッシュは誰も読まないので消す
    try {
      localStorage.removeItem(`rewind_products_${this.collectionHandle}`);
      localStorage.removeItem(`rewind_products_v2_${this.collectionHandle}_${this.country || ''}`);
    } catch {}

    const locale = (config.locale || 'ja').toLowerCase();
    const isEn   = locale.startsWith('en');

    this.fields = isEn
      ? [
          { key: 'weight',   label: 'Weight',   unit: 'g',  step: 0.1 },
          { key: 'diameter', label: 'Diameter',  unit: 'mm', step: 0.1 },
          { key: 'width',    label: 'Width',     unit: 'mm', step: 0.1 },
        ]
      : [
          { key: 'weight',   label: '重量', unit: 'g',  step: 0.1 },
          { key: 'diameter', label: '直径', unit: 'mm', step: 0.1 },
          { key: 'width',    label: '幅',   unit: 'mm', step: 0.1 },
        ];

    this.text = isEn
      ? {
          loading:     'Loading...',
          filtering:   'Filtering…',
          empty:       'No products match these ranges. Try widening weight, diameter or width.',
          emptySoldOut: 'No products are sold out right now.',
          reset:       'Reset weight, diameter and width',
          rangeSep:    '–',
          pagination:  'Pagination',
          lower:       label => `Minimum ${label.toLowerCase()}`,
          upper:       label => `Maximum ${label.toLowerCase()}`,
          showResults: count => `Show ${count} ${count === 1 ? 'product' : 'products'}`,
          found:       count => `${count} ${count === 1 ? 'product' : 'products'} found`,
          sorted:      name => `Sorted by: ${name}.`,
        }
      : {
          loading:     '読み込み中...',
          filtering:   '絞り込み中…',
          empty:       '条件に合う商品が見つかりませんでした。重量・直径・幅の範囲を広げてみてください。',
          emptySoldOut: '在庫切れの商品はありません。',
          reset:       '重量・直径・幅の絞り込みを解除',
          rangeSep:    '〜',
          pagination:  'ページ送り',
          lower:       label => `${label}の下限`,
          upper:       label => `${label}の上限`,
          showResults: count => `${count}件を表示`,
          found:       count => `${count}件の商品が見つかりました`,
          sorted:      name => `${name}に並べ替えました。`,
        };
  }

  // ─────────────────────────────────────────────
  // 初回初期化（データ取得 + 全スライダー構築）
  // ─────────────────────────────────────────────
  async init() {
    this._showLoading();

    // URL がスペック表示になる条件（範囲・在庫切れだけ・重量などの並び順）なら、一覧を薄くしておき、
    // スライダー用のデータと並行して、全件の並びも先に取りに行く
    if (this._urlHasRanges() || this._soldOutOnly() || this._specSort()) {
      this._setPending(true);
      const state = this._readState();
      if (state.supported && (state.filters.length > 0 || state.sortKey !== 'COLLECTION_DEFAULT')) this._getList(state);
    }

    try {
      const cached = this.getCache();
      if (cached) {
        this.products = cached;
        this._loaded  = true;
        this._buildAllPanels();
        this.applyFilter();
        this.refreshInBackground();
      } else {
        const fresh = await this.fetchAllProducts();
        if (fresh) {
          this.products = fresh;
          this.setCache(fresh);
        }
        this._loaded = true;
        this._buildAllPanels();
        this.applyFilter();
      }
    } catch (err) {
      console.error('[RewindFilter] init failed:', err);
      this._loaded = true;
      this._setPending(false);
    }
  }

  // ─────────────────────────────────────────────
  // テーマが一覧を描き直した後の更新（データ再取得なし）
  // ─────────────────────────────────────────────
  refresh() {
    // テーマはページを開いた直後にも theme:loading:end を出す。商品データがまだ無いうちに applyFilter を通すと、
    // 「全件を取得できなかった」扱いになり、並び順のメニューが既定の表示に書き換わる（一覧は後から重量順になる）。
    // 取得が終わるまでは何もしない。終われば init が、その時点の URL でスライダーと一覧を作る
    if (!this._loaded) {
      this._fromHistory = false;
      if (this._urlHasRanges() || this._soldOutOnly() || this._specSort()) this._setPending(true);
      return;
    }

    // 戻る/進むのときだけ、URL の範囲とページを正とする。
    // それ以外（並び順・表示数・絞り込みの変更、カート追加）では URL を読まない。theme.js が持っている URL は
    // こちらの replaceState を知らないので、テーマが pushState した URL には古い範囲・古いページが入っている
    if (this._fromHistory) {
      this._fromHistory = false;
      const params = new URLSearchParams(location.search);
      for (const [key, data] of Object.entries(this.sliderDataRanges)) {
        const min = parseFloat(params.get(`${key}_min`));
        const max = parseFloat(params.get(`${key}_max`));
        this.sliderRanges[key] = { // URL に無い項目は初期位置へ
          min: isNaN(min) ? data.min : Math.max(data.min, Math.min(min, data.max)),
          max: isNaN(max) ? data.max : Math.max(data.min, Math.min(max, data.max)),
        };
      }
      this.page = this._pageFromUrl();
      this._listState = null;
    }

    const mobileApp = document.querySelector('.rewind-filter-app--mobile');
    if (mobileApp) this._buildPanel(mobileApp, /* syncValues= */ true);
    this._syncSliders();
    this.applyFilter();
  }

  // ─────────────────────────────────────────────
  // Storefront API
  // ─────────────────────────────────────────────
  // スライダー用の全商品のスペック値と、買えるかどうか。途中で失敗したら null（欠けたデータを全件として扱わない）
  async fetchAllProducts() {
    const { items, complete } = await this.fetchProducts({ withSpecs: true });
    return complete ? items : null;
  }

  // コレクションの商品を並び順どおりに全件取得する。途中で失敗したら complete: false
  async fetchProducts({ filters = null, sortKey = 'COLLECTION_DEFAULT', reverse = false, withSpecs = false } = {}) {
    const query = `
      query($handle: String!, $after: String, $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean, $country: CountryCode, $language: LanguageCode)
      @inContext(country: $country, language: $language) {
        collection(handle: $handle) {
          products(first: 250, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
            pageInfo { hasNextPage endCursor }
            edges {
              node {
                handle
                onlineStoreUrl
                ${withSpecs ? `
                availableForSale
                weight:   metafield(namespace: "global", key: "weight")   { value }
                diameter: metafield(namespace: "global", key: "diameter") { value }
                width:    metafield(namespace: "global", key: "width")    { value }` : ''}
              }
            }
          }
        }
      }
    `;

    let items = [], cursor = null, hasNext = true;
    while (hasNext) {
      const page = await this.fetchPage(query, { filters, sortKey, reverse, after: cursor });
      if (!page) return { items, complete: false };
      const edges = page.collection?.products?.edges ?? [];
      for (const e of edges) {
        // オンラインストアに出ていない商品は、一覧にもカードにも出せないので除く
        if (!e.node.onlineStoreUrl) continue;
        items.push(withSpecs
          ? {
              handle:   e.node.handle,
              available: e.node.availableForSale,
              weight:   e.node.weight?.value   ?? null,
              diameter: e.node.diameter?.value ?? null,
              width:    e.node.width?.value    ?? null,
            }
          : e.node.handle);
      }
      hasNext = page.collection?.products?.pageInfo?.hasNextPage ?? false;
      cursor  = page.collection?.products?.pageInfo?.endCursor   ?? null;
    }
    return { items, complete: true };
  }

  async fetchPage(query, variables) {
    try {
      const res = await fetch(
        `https://${this.storeDomain}/api/${REWIND_API_VERSION}/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({
            query,
            variables: { handle: this.collectionHandle, country: this.country, language: this.language, ...variables },
          }),
        }
      );
      if (!res.ok) { console.error('[RewindFilter] API error:', res.status); return null; }
      const json = await res.json();
      if (json.errors) { console.error('[RewindFilter] GraphQL errors:', json.errors); return null; }
      return json.data;
    } catch (err) {
      console.error('[RewindFilter] fetch failed:', err);
      return null;
    }
  }

  async refreshInBackground() {
    const fresh = await this.fetchAllProducts();
    if (!fresh?.length) return;
    // 中身が同じでも保存し直す（保存した時刻を更新する。古いままだと、確かめたばかりのデータが期限切れになる）
    this.setCache(fresh);
    if (JSON.stringify(this.products) === JSON.stringify(fresh)) return;
    const hadPanels = !!document.querySelector('.rewind-slider-track');
    this.products = fresh;
    if (!hadPanels) this._buildAllPanels(); // 空のキャッシュから回復したとき
    this._renderedKey = null;
    this.applyFilter();
  }

  // ─────────────────────────────────────────────
  // キャッシュ
  // ─────────────────────────────────────────────
  getCache() {
    try {
      const raw = localStorage.getItem(this.cacheKey);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp > REWIND_PRODUCTS_CACHE_TTL) return null;
      return data;
    } catch { return null; }
  }

  setCache(data) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
    } catch {}
  }

  // 直近の1条件ぶんだけ sessionStorage に残す（商品ページから戻ったときに取り直さない）
  _getStoredList(key) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.listCacheKey));
      if (!stored || stored.key !== key || Date.now() - stored.timestamp > this.cacheTTL) return null;
      return stored.data;
    } catch { return null; }
  }

  _storeList(key, data) {
    try {
      sessionStorage.setItem(this.listCacheKey, JSON.stringify({ key, data, timestamp: Date.now() }));
    } catch {}
  }

  // ─────────────────────────────────────────────
  // ローディング表示
  // ─────────────────────────────────────────────
  _showLoading() {
    document.querySelectorAll('.rewind-filter-app').forEach(container => {
      container.innerHTML = `<p class="rewind-loading">${this.text.loading}</p>`;
    });
  }

  // ─────────────────────────────────────────────
  // UI構築
  // ─────────────────────────────────────────────
  _buildAllPanels() {
    document.querySelectorAll('.rewind-filter-app').forEach(container => {
      this._buildPanel(container, false);
    });
  }

  _buildPanel(container, syncValues) {
    container.innerHTML = '';

    const hasData = this.fields.some(field => {
      return this.products.some(p => p[field.key] !== null);
    });
    if (!hasData) return;

    // noUiSlider は作成時にも update を出す。その分では絞り込みを走らせない
    this._building = true;

    for (const field of this.fields) {
      const values = this.products
        .map(p => parseFloat(p[field.key]))
        .filter(v => !isNaN(v));
      if (values.length === 0) continue;

      const dataMin = Math.floor(Math.min(...values) * 10) / 10;
      const dataMax = Math.ceil(Math.max(...values)  * 10) / 10;

      if (dataMin === dataMax) continue;

      // データ範囲を常に記録（タッチ判定に使用）
      this.sliderDataRanges[field.key] = { min: dataMin, max: dataMax };

      const urlParams = new URLSearchParams(location.search);
      const urlMin = parseFloat(urlParams.get(`${field.key}_min`));
      const urlMax = parseFloat(urlParams.get(`${field.key}_max`));
      // URL の値を使うのは初回だけ。作り直しのときは手元の値（refresh のコメントを参照）
      const hasUrlMin = !syncValues && !isNaN(urlMin);
      const hasUrlMax = !syncValues && !isNaN(urlMax);

      const startMin = hasUrlMin ? Math.max(dataMin, Math.min(urlMin, dataMax))
                     : syncValues ? (this.sliderRanges[field.key]?.min ?? dataMin)
                     : dataMin;
      const startMax = hasUrlMax ? Math.max(dataMin, Math.min(urlMax, dataMax))
                     : syncValues ? (this.sliderRanges[field.key]?.max ?? dataMax)
                     : dataMax;

      this.sliderRanges[field.key] = { min: startMin, max: startMax };

      const range = this._sliderRange(values, dataMin, dataMax, field.step);
      this._createSlider(container, field, dataMin, dataMax, startMin, startMax, range);
    }

    this._building = false;
  }

  // noUiSlider の range（目盛り）を作る。
  // 等間隔だと、付属品（数g）やセット商品（100g超）が範囲を広げ、ヨーヨー本体が集まる帯がトラックの数pxに潰れる。
  // 実測（/collections/all・トラック幅205px）: 重量 2.6〜138g の等間隔では 64〜66g がつまみ3px分で、
  // 2つのつまみが重なって掴めない。帯を中央70%に広げると約19px離れる。
  // 目盛りが変わるのはトラック上の位置だけで、値・入力欄・絞り込みの結果は変わらない
  _sliderRange(values, dataMin, dataMax, step) {
    const linear = { min: dataMin, max: dataMax };
    if (values.length < REWIND_SCALE_MIN_VALUES) return linear;

    const sorted = [...values].sort((a, b) => a - b);
    const at     = p => sorted[Math.round((sorted.length - 1) * p)];
    const low    = Math.floor(at(REWIND_SCALE_LOW_PERCENTILE) * 10) / 10;
    const high   = Math.ceil(at(REWIND_SCALE_HIGH_PERCENTILE) * 10) / 10;

    // 帯の端がデータの端と重なる側は、区切りを置かない（外れた値が無い側）
    const hasLow  = low > dataMin;
    const hasHigh = high < dataMax;
    const from    = hasLow ? low : dataMin;
    const to      = hasHigh ? high : dataMax;
    if (!(from < to) || (!hasLow && !hasHigh)) return linear;

    const trackShare = ((hasHigh ? REWIND_SCALE_HIGH_POSITION : 100) - (hasLow ? REWIND_SCALE_LOW_POSITION : 0)) / 100;
    const valueShare = (to - from) / (dataMax - dataMin);
    if (trackShare / valueShare < REWIND_SCALE_MIN_GAIN) return linear;

    // 区切りを入れると、noUiSlider の step は最初の区間にしか効かない。区間ごとに指定する
    const range = { min: [dataMin, step], max: [dataMax] };
    if (hasLow)  range[`${REWIND_SCALE_LOW_POSITION}%`]  = [low, step];
    if (hasHigh) range[`${REWIND_SCALE_HIGH_POSITION}%`] = [high, step];
    return range;
  }

  _createSlider(container, field, dataMin, dataMax, startMin, startMax, range) {
    const scope   = container.classList.contains('rewind-filter-app--mobile') ? 'mobile' : 'desktop';
    const labelId = `rewind-slider-label-${scope}-${field.key}`;

    const group = document.createElement('div');
    group.className = 'rewind-slider-group';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', labelId);

    // ラベル（フィールド名のみ）
    const label = document.createElement('p');
    label.className = 'collection__filter-group-name link link--secondary text--strong rewind-slider-label';
    label.id = labelId;
    label.textContent = field.label;
    group.appendChild(label);

    // スライダートラック
    const sliderEl = document.createElement('div');
    sliderEl.className = 'rewind-slider-track';
    sliderEl.dataset.field = field.key;
    group.appendChild(sliderEl);

    // 入力欄
    const inputRow = document.createElement('div');
    inputRow.className = 'rewind-input-row';

    const minInput = document.createElement('input');
    minInput.type = 'number';
    minInput.className = 'rewind-input';
    minInput.step = field.step;
    minInput.min  = dataMin;
    minInput.max  = dataMax;
    minInput.value = startMin;
    minInput.inputMode = 'decimal';
    minInput.setAttribute('aria-label', `${this.text.lower(field.label)} (${field.unit})`);

    const minUnit = document.createElement('span');
    minUnit.className = 'rewind-input-unit';
    minUnit.textContent = field.unit;

    const sep = document.createElement('span');
    sep.className = 'rewind-input-sep';
    sep.textContent = this.text.rangeSep;

    const maxInput = document.createElement('input');
    maxInput.type = 'number';
    maxInput.className = 'rewind-input';
    maxInput.step = field.step;
    maxInput.min  = dataMin;
    maxInput.max  = dataMax;
    maxInput.value = startMax;
    maxInput.inputMode = 'decimal';
    maxInput.setAttribute('aria-label', `${this.text.upper(field.label)} (${field.unit})`);

    const maxUnit = document.createElement('span');
    maxUnit.className = 'rewind-input-unit';
    maxUnit.textContent = field.unit;

    inputRow.appendChild(minInput);
    inputRow.appendChild(minUnit);
    inputRow.appendChild(sep);
    inputRow.appendChild(maxInput);
    inputRow.appendChild(maxUnit);
    group.appendChild(inputRow);

    container.appendChild(group);

    noUiSlider.create(sliderEl, {
      start:   [startMin, startMax],
      connect: true,
      step:    field.step,
      range,
      format: {
        to:   v => Math.round(v * 10) / 10,
        from: v => parseFloat(v),
      },
      // スクリーンリーダー向け: つまみの名前と、単位つきの値
      handleAttributes: [
        { 'aria-label': this.text.lower(field.label) },
        { 'aria-label': this.text.upper(field.label) },
      ],
      ariaFormat: { to: v => `${Math.round(v * 10) / 10} ${field.unit}` },
    });

    // スライダー変更 → 入力欄・ranges 更新
    sliderEl.noUiSlider.on('update', (values) => {
      const [lo, hi] = values;

      minInput.value = lo;
      maxInput.value = hi;

      if (this._building) return;

      this.sliderRanges[field.key] = { min: parseFloat(lo), max: parseFloat(hi) };

      if (this._syncing) return;

      // 他コンテナのスライダーを同期
      this._syncing = true;
      document.querySelectorAll('.rewind-filter-app').forEach(otherContainer => {
        if (otherContainer === container) return;
        const otherSlider = otherContainer.querySelector(`.rewind-slider-track[data-field="${field.key}"]`);
        if (otherSlider?.noUiSlider) {
          otherSlider.noUiSlider.set([lo, hi]);
        }
      });
      this._syncing = false;

      // 範囲が変わったら1ページ目から
      this.page = 1;
      this._scheduleApply();
    });

    // 入力欄変更 → スライダー更新
    minInput.addEventListener('change', () => {
      let val = parseFloat(minInput.value);
      if (isNaN(val)) val = dataMin;
      val = Math.max(dataMin, Math.min(val, parseFloat(maxInput.value)));
      sliderEl.noUiSlider.set([val, null]);
    });

    maxInput.addEventListener('change', () => {
      let val = parseFloat(maxInput.value);
      if (isNaN(val)) val = dataMax;
      val = Math.min(dataMax, Math.max(val, parseFloat(minInput.value)));
      sliderEl.noUiSlider.set([null, val]);
    });
  }

  // 全スライダーを sliderRanges の値に合わせる（戻る/進むで URL の値が変わったとき用）
  _syncSliders() {
    this._building = true;
    document.querySelectorAll('.rewind-slider-track').forEach(sliderEl => {
      const range = this.sliderRanges[sliderEl.dataset.field];
      if (range && sliderEl.noUiSlider) sliderEl.noUiSlider.set([range.min, range.max]);
    });
    this._building = false;
  }

  resetSliders() {
    for (const [key, range] of Object.entries(this.sliderDataRanges)) {
      const sliderEl = document.querySelector(`.rewind-slider-track[data-field="${key}"]`);
      if (sliderEl?.noUiSlider) sliderEl.noUiSlider.set([range.min, range.max]);
    }
  }

  resetSlider(key) {
    const range    = this.sliderDataRanges[key];
    const sliderEl = document.querySelector(`.rewind-slider-track[data-field="${key}"]`);
    if (range && sliderEl?.noUiSlider) sliderEl.noUiSlider.set([range.min, range.max]);
  }

  // テーマの「すべて消去」が押されたとき用。値だけ初期位置に戻す
  // （一覧の描き直しは、テーマの再読み込み → refresh に任せる）
  clearRanges() {
    for (const [key, range] of Object.entries(this.sliderDataRanges)) {
      this.sliderRanges[key] = { ...range };
    }
    this._syncSliders();
  }

  // ─────────────────────────────────────────────
  // 現在の状態
  // ─────────────────────────────────────────────
  // 初期位置から動いている項目
  _touchedFields() {
    return this.fields
      .map(field => ({ ...field, ...this.sliderRanges[field.key], data: this.sliderDataRanges[field.key] }))
      .filter(f => f.data && (f.min > f.data.min || f.max < f.data.max));
  }

  _isTouched() {
    // スライダーがデフォルト範囲から動いているか（タッチ判定）
    return Object.entries(this.sliderRanges).some(([key, { min, max }]) => {
      const dataRange = this.sliderDataRanges[key];
      return dataRange && (min > dataRange.min || max < dataRange.max);
    });
  }

  // 「在庫切れ」だけが選ばれているか（「在庫あり」と両方なら、Shopify は全件を返す。絞り込んでいないのと同じ）。
  // Shopify の在庫の絞り込みは次の2点で、カードの「売り切れ」表示と合わない。
  //   - バリエーション単位。1色でも切れていれば「在庫切れ」に入る（/collections/all で 426件中 357件は買える商品だった）
  //   - 全ロケーションの在庫を見る。渋谷店にだけ在庫がある商品は、オンラインでは売り切れなのに「在庫あり」に入る
  // そのため、このときは API に在庫の条件を渡さず、取得済みの available（＝カードの表示と同じ判定）で絞る
  _soldOutOnly() {
    const values = new URLSearchParams(location.search).getAll('filter.v.availability');
    return values.includes('0') && !values.includes('1');
  }

  // 「在庫あり」だけが選ばれているか。こちらはスペック表示にはしない（一覧は main-collection.liquid が
  // 買えない商品を飛ばして出す）。スライダーと併用したときに、同じ商品を結果から外すために見る
  _inStockOnly() {
    const values = new URLSearchParams(location.search).getAll('filter.v.availability');
    return values.includes('1') && !values.includes('0');
  }

  // 並び順が重量・直径・幅のどれかなら { key, reverse }。
  // Shopify はこの sort_by を知らないので、サーバーの一覧は既定の並びで出ている。
  // 取得済みの数値でコレクション全体を並べ替えて、スペック表示に差し替える
  _specSort() {
    const sort = this._requestedSpecSort();
    if (!sort) return null;
    // 並べ替えられないとき（メニューにも出していない）は、通常の一覧のまま。
    //   - タグ付き URL: 全件を取得できない（_readState を参照）
    //   - 数値を持つ商品が足りない項目・商品データを取得できなかったとき（_sortable を参照）
    // 取得が終わるまでは判断できないので、並べ替える前提で待つ
    if (this.settings.currentTags?.length) return null;
    if (this._loaded && !this._sortable(sort[0])) return null;
    return { key: sort[0], reverse: sort[1] };
  }

  // URL の sort_by が重量・直径・幅の並び順なら [項目, 大きい順か]
  _requestedSpecSort() {
    return rewindLookup(REWIND_SPEC_SORTS, new URLSearchParams(location.search).get('sort_by') ?? '');
  }

  // 数値を持つ商品が2つ以上あるか（ストリングだけのコレクションの重量順などは、並べ替えても何も変わらない）
  _sortable(key) {
    return this.products.filter(p => !isNaN(parseFloat(p[key]))).length >= 2;
  }

  // スペック表示にする条件
  _isActive() {
    return this._isTouched() || this._soldOutOnly() || !!this._specSort();
  }

  // 操作済みのスライダーについて、範囲内だと確認できない商品は除外する。
  // meta が undefined の商品（＝Storefront未公開などで取得データに無い）もここで除外される。
  // これをしないと上限を超えた商品が絞り込みを素通りして常に表示されてしまう
  // （例：直径上限52mmでも「モズ 7075」55mmが残る）。
  _matches(meta) {
    for (const [key, { min, max }] of Object.entries(this.sliderRanges)) {
      const dataRange = this.sliderDataRanges[key];
      const isTouched = dataRange && (min > dataRange.min || max < dataRange.max);
      if (!isTouched) continue;

      const val = meta ? parseFloat(meta[key]) : NaN;
      if (isNaN(val) || val < min || val > max) return false;
    }
    return true;
  }

  _section() {
    return document.querySelector('[data-section-type="collection"]');
  }

  _pageFromUrl() {
    const page = parseInt(new URLSearchParams(location.search).get('spec_page'), 10);
    return page > 0 ? page : 1;
  }

  _urlHasRanges() {
    const params = new URLSearchParams(location.search);
    return this.fields.some(f => params.has(`${f.key}_min`) || params.has(`${f.key}_max`));
  }

  // URL の絞り込み・並び順と、表示数を Storefront API の引数に読み替える
  _readState() {
    const params  = new URLSearchParams(location.search);
    const filters = [], price = {};
    const soldOutOnly = this._soldOutOnly();
    let supported = true;

    for (const [name, value] of params) {
      if (!name.startsWith('filter.') || value === '') continue;
      const parts = name.split('.'); // filter . p|v . 種別 . ...

      if (name === 'filter.v.price.gte')            price.min = parseFloat(value);
      else if (name === 'filter.v.price.lte')       price.max = parseFloat(value);
      else if (name === 'filter.v.availability')    { if (!soldOutOnly) filters.push({ available: value === '1' }); }
      else if (name === 'filter.p.vendor')          filters.push({ productVendor: value });
      else if (name === 'filter.p.product_type')    filters.push({ productType: value });
      else if (name === 'filter.p.tag')             filters.push({ tag: value });
      else if (name.startsWith('filter.v.option.')) filters.push({ variantOption: { name: parts.slice(3).join('.'), value } });
      else if (parts[2] === 'm' && parts.length >= 5) {
        const metafield = { namespace: parts[3], key: parts.slice(4).join('.'), value };
        filters.push(parts[1] === 'v' ? { variantMetafield: metafield } : { productMetafield: metafield });
      } else {
        supported = false;
      }
    }
    if (price.min != null || price.max != null) filters.push({ price });

    // /collections/<handle>/<tag> のタグ絞り込みは API で再現できない
    // （Search & Discovery に無い絞り込みを渡しても、エラーにならず無視されて全件が返る）
    if (this.settings.currentTags?.length) supported = false;

    const sortBy    = params.get('sort_by');
    const isDefault = !sortBy || sortBy === this.settings.defaultSortBy;
    const [sortKey, reverse] = (!isDefault && rewindLookup(REWIND_SORT_KEYS, sortBy)) || ['COLLECTION_DEFAULT', false];

    const selected = document.querySelector('#display-by-selector .value-picker__choice-item.is-selected');
    const perPage  = parseInt(selected?.dataset.value, 10) || 48;

    // 重量・直径・幅の並び順のときは、sortKey は既定のまま（数値が同じ商品・未登録の商品は、既定の並びの順に出す）
    const specSort = this._specSort();

    return { filters, sortKey, reverse, perPage, supported, soldOutOnly, specSort, inStockOnly: this._inStockOnly(), listKey: JSON.stringify([filters, sortKey, reverse]) };
  }

  // 絞り込み条件 + 並び順での、コレクション全体の商品ハンドルの並び。取得できなければ null
  _getList(state) {
    // 絞り込みなし・既定の並び順なら、スライダー用に取得済みのデータがそのまま使える
    if (state.filters.length === 0 && state.sortKey === 'COLLECTION_DEFAULT') {
      return Promise.resolve(this.products.map(p => p.handle));
    }

    if (!this.listCache.has(state.listKey)) {
      const stored = this._getStoredList(state.listKey);
      const request = stored
        ? Promise.resolve(stored)
        : this.fetchProducts({ filters: state.filters, sortKey: state.sortKey, reverse: state.reverse }).then(({ items, complete }) => {
            if (!complete) { this.listCache.delete(state.listKey); return null; }
            this._storeList(state.listKey, items);
            return items;
          });
      this.listCache.set(state.listKey, request);
    }
    return this.listCache.get(state.listKey);
  }

  // ─────────────────────────────────────────────
  // フィルタロジック
  // ─────────────────────────────────────────────
  _scheduleApply() {
    clearTimeout(this._applyTimer);
    this._applyTimer = setTimeout(() => this.applyFilter(), REWIND_APPLY_DELAY);
  }

  async applyFilter() {
    clearTimeout(this._applyTimer);
    const seq = ++this._applySeq;

    // テーマが一覧を描き直していたら、差し込んだ要素はもう無い
    if (this._spec && !this._spec.list.isConnected) {
      this._spec = null;
      this._renderedKey = null;
    }

    this._renderIndicators();
    this._syncSortPicker();

    if (!this._isActive()) {
      this._unmountSpec();
      this._applyInPage();
      this._lastTotal = null;
      // 次にスペック表示へ入るときは1ページ目から（「在庫切れ」を外して入れ直したとき）
      this.page = 1;
      this._setPending(false);
      this._updateUrl();
      return;
    }

    const state = this._readState();

    // 絞り込み・並び順・表示数が変わったら1ページ目から
    const listState = JSON.stringify([state.listKey, state.perPage, state.soldOutOnly, state.specSort]);
    if (this._listState !== null && this._listState !== listState) this.page = 1;
    this._listState = listState;

    const key = JSON.stringify([state.listKey, this.sliderRanges, this.page, state.perPage, state.soldOutOnly, state.specSort]);
    if (this._spec && this._renderedKey === key) {
      this._setPending(false);
      return;
    }

    // ここから先は一覧を書き換える。途中で元の条件に戻されても「描画済み」と見なさない
    this._renderedKey = null;
    this._setPending(true);

    let rendered = false;
    // 在庫切れの判定と並べ替えは、取得済みの商品データで行う。データが無いときに「0件」と出さない
    const list = state.supported && this.products.length > 0 ? await this._getList(state) : null;
    if (seq !== this._applySeq) return;

    if (list) {
      const metaMap = this._metaMap();
      // 在庫あり: API の available: true は渋谷店だけの在庫も数えるので、買えない商品をここで外す
      const matched = list.filter(handle =>
        this._matches(metaMap[handle])
        && (!state.soldOutOnly || metaMap[handle]?.available === false)
        && (!state.inStockOnly || metaMap[handle]?.available !== false));

      // 並び順だけでスペック表示にしていて0件なら、並べ替えるものが無い。
      // サーバーの一覧（0件の案内を含む）をそのまま使う。取得の失敗ではないので、メニューの表示は変えない
      if (state.specSort && !state.soldOutOnly && !this._isTouched() && matched.length === 0) {
        this._unmountSpec();
        this._lastTotal = null;
        this.page = 1;
        this._setPending(false);
        this._updateUrl();
        return;
      }

      const sorted = state.specSort ? this._sortBySpec(matched, metaMap, state.specSort) : matched;
      rendered = await this._renderSpec(sorted, state, seq);
      if (seq !== this._applySeq) return;
    }

    if (!rendered) {
      // 全件の並びやカードを取得できないとき（API・カード取得の失敗、読み替えられない絞り込み）は、
      // 表示中の商品の中だけで絞り込む
      console.warn('[RewindFilter] コレクション全体から絞り込めないため、表示中の商品の中で絞り込みます');
      this._unmountSpec();
      this._applyInPage();
      if (state.specSort) this._syncSortPicker(/* failed= */ true);
      // 全体の件数は分からない
      this._lastTotal = null;
      this._section()?.querySelector('.rewind-spec-recap .collection__mobile-active-filters-results')?.replaceChildren();
      this._setPending(false);
      this._updateUrl();
      return;
    }

    this._renderedKey = key;
    this._setPending(false);
    this._updateUrl();
  }

  // ハンドル → 商品データ（重量・直径・幅・買えるかどうか）。商品データが入れ替わったら作り直す
  _metaMap() {
    if (this._metaSource !== this.products) {
      this._metaSource = this.products;
      this._metaByHandle = {};
      for (const p of this.products) this._metaByHandle[p.handle] = p;
    }
    return this._metaByHandle;
  }

  // 重量・直径・幅で並べている間は、その数値をカードに出す（「重量 65.2 g」）。
  // 軽い順の先頭には数gの付属品が並ぶなど、数値が見えないと並びの理由が分からないため。未登録の商品には何も出さない。
  // 並び順で項目が変わるので、カードの HTML（cardCache）には入れず、一覧に出すたびに付ける
  _applySpecValues(root, specSort) {
    const field = specSort && this.fields.find(f => f.key === specSort.key);
    if (!field) return;

    const metaMap = this._metaMap();
    root.querySelectorAll('.product-item[data-rewind-handle]').forEach(card => {
      if (card.querySelector('.rewind-spec-value')) return;

      const value = parseFloat(metaMap[card.dataset.rewindHandle]?.[field.key]);
      const title = card.querySelector('.product-item__title');
      if (isNaN(value) || !title) return;

      const el = document.createElement('span');
      el.className = 'rewind-spec-value';
      el.append(`${field.label} `);
      const number = document.createElement('span');
      number.className = 'rewind-spec-value__number';
      number.textContent = `${value} ${field.unit}`;
      el.append(number);

      // テーマの設定によっては、タイトルの直後にメーカー名が来る（.product-item__title + .product-item__vendor の余白を崩さない）
      const next = title.nextElementSibling;
      (next?.matches('.product-item__vendor') ? next : title).after(el);
    });
  }

  // 重量・直径・幅で並べ替える。数値が未登録の商品は、向きに関係なく末尾にまとめる（店主の判断、2026-10-10）。
  // 数値が同じ商品どうしと、未登録の商品どうしは、元の並び（コレクションの既定の並び）のまま
  _sortBySpec(handles, metaMap, { key, reverse }) {
    const withValue = [], without = [];
    for (const handle of handles) {
      const value = parseFloat(metaMap[handle]?.[key]);
      if (isNaN(value)) without.push(handle);
      else withValue.push({ handle, value });
    }
    withValue.sort((a, b) => reverse ? b.value - a.value : a.value - b.value);
    return withValue.map(item => item.handle).concat(without);
  }

  // 並び順のメニュー（テーマが描き直すたびに作り直されるので、applyFilter のたびに合わせる）。
  //   - 並べ替えられない項目は出さない（_sortable を参照）
  //   - URL が重量順などのとき、選択中の表示を実際の一覧に合わせる。
  //     並べ替えている: URL の並び順 / 並べ替えていない（全件を取得できなかった = failed・並べ替えられない項目）: 既定の並び順。
  //     一度 failed になった後で取得できたときに戻せるよう、どちらの向きにも付け直す
  _syncSortPicker(failed = false) {
    const picker = document.getElementById('sort-by-selector');
    if (!picker) return;

    let shown = 0;
    picker.querySelectorAll('[data-rewind-sort-field]').forEach(option => {
      option.hidden = !this._sortable(option.dataset.rewindSortField);
      if (!option.hidden) shown++;
    });
    // 標準の並び順との間の区切り線。重量・直径・幅の項目が1つも無ければ出さない
    const divider = picker.querySelector('[data-rewind-sort-divider]');
    if (divider) divider.hidden = shown === 0;

    if (!this._requestedSpecSort()) return;

    const sorted = !failed && !!this._specSort();
    const value  = sorted ? new URLSearchParams(location.search).get('sort_by') : this.settings.defaultSortBy;
    const option = [...picker.querySelectorAll('[data-action="select-value"]')].find(item => item.dataset.value === value);
    if (!option || option.classList.contains('is-selected')) return;

    picker.querySelectorAll('.is-selected').forEach(item => {
      item.classList.remove('is-selected');
      item.removeAttribute('aria-current');
    });
    option.classList.add('is-selected');
    option.setAttribute('aria-current', 'true');
    const name = document.querySelector('[aria-controls="sort-by-selector"] [data-rewind-sort-name]');
    if (name) name.textContent = option.textContent.trim();
  }

  // 表示中のカードを show/hide する（スペック表示に切り替えられないときの代替）
  _applyInPage() {
    document.querySelectorAll('[data-rewind-handle]').forEach(card => {
      card.style.display = '';
    });
    if (!this._isTouched()) return;

    const metaMap = this._metaMap();

    document.querySelectorAll('[data-rewind-handle]').forEach(card => {
      card.style.display = this._matches(metaMap[card.dataset.rewindHandle]) ? '' : 'none';
    });
  }

  // ─────────────────────────────────────────────
  // スペック表示（コレクション全体からの絞り込み結果）
  // ─────────────────────────────────────────────
  // 描画できたら true。カードを取得できなかったら false（呼び出し側が代替動作に落とす）
  async _renderSpec(matched, state, seq) {
    // サーバー側の結果が0件だと、一覧そのものが無い。在庫切れの絞り込みでは、サーバーが0件でも
    // こちらには該当があり得る（渋谷店にだけ在庫がある商品。_soldOutOnly を参照）ので、0件の案内と差し替える
    if (!this._mountSpec(state.soldOutOnly && matched.length > 0)) return true;

    const { list } = this._spec;
    const total = matched.length;
    const pages = Math.max(1, Math.ceil(total / state.perPage));
    this.page   = Math.min(this.page, pages);

    const start   = (this.page - 1) * state.perPage;
    const handles = matched.slice(start, start + state.perPage);

    this._lastTotal = total;
    this._renderCounts(total, start, handles.length);
    this._renderPagination(pages);

    if (total === 0) {
      // スライダーを動かしていない（在庫切れの絞り込みだけで0件）なら、範囲を広げる案内は出さない。
      // ボタンはテーマの「すべて消去」（main-collection.liquid の0件画面と同じ）
      const byRanges = this._isTouched();
      const message  = byRanges ? this.text.empty : this.text.emptySoldOut;
      const sortBy   = new URLSearchParams(location.search).get('sort_by');
      const clearUrl = location.pathname + (sortBy ? `?sort_by=${encodeURIComponent(sortBy)}` : '');
      const button   = byRanges
        ? `<button type="button" class="empty-state__button button button--primary" data-action="reset-rewind-sliders">${this._escape(this.text.reset)}</button>`
        : `<button type="button" class="empty-state__button button button--primary" data-action="clear-filters" data-url="${this._escape(clearUrl)}">${this._escape(this.settings.strings?.clearAll ?? '')}</button>`;

      this._announce(message);
      list.style.minHeight = '';
      list.classList.add('rewind-spec-list--empty');
      list.innerHTML = `
        <div class="empty-state empty-state--tight">
          <p class="empty-state__description">${this._escape(message)}</p>
          <div class="empty-state__button-container">${button}</div>
        </div>`;
      return true;
    }

    // 取得済みのカードから順に出す。前の結果は、最初のカードが届くまで残しておく
    const loader = document.createElement('p');
    loader.className = 'rewind-loading rewind-spec-loading';
    loader.textContent = this.text.loading;

    let painted = 0, cleared = false;
    const paint = (final) => {
      if (seq !== this._applySeq || !list.isConnected) return;

      let html = '';
      while (painted < handles.length && (final || this.cardCache.has(handles[painted]))) {
        html += this.cardCache.get(handles[painted++]) ?? '';
      }

      if (!cleared && (html || final || list.childElementCount === 0)) {
        // カードが増えていく間にページの高さが縮んで、スクロール位置が動かないようにする
        list.style.minHeight = `${list.offsetHeight}px`;
        list.classList.remove('rewind-spec-list--empty');
        list.innerHTML = '';
        list.appendChild(loader);
        cleared = true;
      }
      if (html) {
        loader.insertAdjacentHTML('beforebegin', html);
        this._applyLayout(list);
        this._applySpecValues(list, state.specSort);
      }
      if (painted === handles.length) {
        loader.remove();
        list.style.minHeight = '';
      }
    };

    paint(false);
    const loaded = await this._loadCards(handles, seq, () => paint(false));
    if (seq !== this._applySeq) return true;
    if (!loaded) return false;
    paint(true);

    // カラースウォッチの「+N」はテーマが resize で数え直す
    if (list.querySelector('.product-item__swatch-list')) {
      window.dispatchEvent(new Event('resize'));
    }

    const pageCount = pages > 1
      ? ' ' + (this.settings.strings?.pageCount ?? '').replace('111111', this.page).replace('222222', pages)
      : '';
    // 重量・直径・幅で並べ替えたときは、並び順の名前も伝える（並び順だけを変えると件数は同じで、文が変わらず読まれない）
    const sortName = state.specSort
      ? document.querySelector('#sort-by-selector .is-selected')?.textContent.trim()
      : '';
    this._announce((sortName ? this.text.sorted(sortName) + ' ' : '') + this.text.found(total) + pageCount);
    return true;
  }

  // サーバーが描画した一覧・ページ送りを退避して、スペック表示用の要素に差し替える
  _mountSpec(replaceEmptyState = false) {
    if (this._spec) return true;

    const section    = this._section();
    const dynamic    = section?.querySelector('.collection__dynamic-part');
    const serverList = dynamic?.querySelector('.product-list--collection')
      ?? (replaceEmptyState ? dynamic?.querySelector('.empty-state--tight') : null);
    if (!serverList) return false;

    // 最初のカードが届くまでは、サーバー側のカードの写しを薄くして残す（一覧を空にしない）
    const list = document.createElement('div');
    if (serverList.matches('.product-list--collection')) {
      list.className = `${serverList.className} rewind-spec-list`;
      list.innerHTML = serverList.innerHTML;
    } else {
      // サーバー側の0件の案内と差し替えるとき（絞り込み中なので、サイドバーつきの一覧）
      list.className = 'product-list product-list--collection product-list--with-sidebar rewind-spec-list';
    }

    const pagination = document.createElement('div');
    pagination.className = 'pagination rewind-spec-pagination';

    const serverPagination = serverList.parentNode.querySelector('.pagination');

    // 隠すのではなく DOM から外す（同じ商品のフォームやラジオの id が重複するため）
    serverList.replaceWith(list);
    if (serverPagination) serverPagination.remove();
    list.after(pagination);

    const counts = new Map();
    section.querySelectorAll(Object.keys(REWIND_COUNT_TARGETS).join(',')).forEach(el => counts.set(el, el.innerHTML));

    this._spec = { list, pagination, serverList, serverPagination, counts };
    return true;
  }

  _unmountSpec() {
    if (!this._spec) return;

    const { list, pagination, serverList, serverPagination, counts } = this._spec;
    this._spec = null;
    this._renderedKey = null;

    // テーマが一覧を描き直した後なら、戻す先はもうサーバー側の最新の一覧になっている
    if (!list.isConnected) return;

    // スペック表示中にグリッド/リストが切り替えられているかもしれない
    this._applyLayout(serverList);
    list.replaceWith(serverList);
    if (serverPagination) pagination.replaceWith(serverPagination);
    else pagination.remove();
    counts.forEach((html, el) => { el.innerHTML = html; });
  }

  // 表示するページ分のカードを取得して cardCache に入れる。1リクエストでも失敗したら false
  async _loadCards(handles, seq, onProgress) {
    // 直前の applyFilter が取得中のカードは、取り直さずにそれを待つ
    const inFlight = new Set(), missing = [];
    for (const handle of handles) {
      if (this.cardCache.has(handle)) continue;
      const request = this.cardRequests.get(handle);
      if (request) inFlight.add(request);
      else missing.push(handle);
    }

    const chunks = [];
    for (let i = 0; i < missing.length; i += REWIND_CARDS_PER_REQUEST) {
      chunks.push(missing.slice(i, i + REWIND_CARDS_PER_REQUEST));
    }

    let next = 0, ok = true;
    const worker = async () => {
      // 条件が変わった後は、まだ始めていない分を取りに行かない
      while (next < chunks.length && seq === this._applySeq) {
        const chunk   = chunks[next++];
        const request = this._fetchCards(chunk);
        chunk.forEach(handle => this.cardRequests.set(handle, request));
        if (!await request) ok = false;
        chunk.forEach(handle => this.cardRequests.delete(handle));
        onProgress();
      }
    };
    await Promise.all([
      ...[...inFlight].map(request => request.then(loaded => { if (!loaded) ok = false; onProgress(); })),
      ...Array.from({ length: Math.min(REWIND_CARDS_CONCURRENCY, chunks.length) }, worker),
    ]);
    return ok;
  }

  async _fetchCards(handles) {
    // collection.sort_by 経由でハンドルを渡す（sections/rewind-product-cards.liquid を参照）
    const url = `${location.pathname}?sort_by=${encodeURIComponent(handles.join(','))}&section_id=${REWIND_CARDS_SECTION}`;
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) { console.error('[RewindFilter] cards error:', res.status); return false; }

      const doc   = new DOMParser().parseFromString(await res.text(), 'text/html');
      const cards = doc.querySelectorAll('[data-rewind-handle]');

      // 1枚も返らないのは、セクションが無い・sort_by 経由の受け渡しが効かなくなった、のどちらか。
      // 空で埋めると、ページを開いている間ずっと一覧が空になるので、失敗として扱う
      if (cards.length === 0) { console.error('[RewindFilter] cards empty'); return false; }

      cards.forEach(card => {
        this._adaptCard(card);
        this.cardCache.set(card.dataset.rewindHandle, card.outerHTML);
      });

      // 一部だけ返ってこなかった商品は空で埋める（同じ商品を何度も取りに行かない）
      handles.forEach(handle => {
        if (!this.cardCache.has(handle)) this.cardCache.set(handle, '');
      });
      return true;
    } catch (err) {
      console.error('[RewindFilter] cards fetch failed:', err);
      return false;
    }
  }

  // カードは別セクションで描画されるので、main-collection のセクションに依存する箇所を合わせる
  _adaptCard(card) {
    const sectionId = this._section()?.dataset.sectionId;
    const { quickBuy, quickView } = this.settings;

    card.querySelectorAll('[data-secondary-action="open-quick-view"]').forEach(button => {
      button.setAttribute('aria-controls', `modal-quick-view-${sectionId}`);
      if (quickView === 'list_grid') button.classList.remove('product-item__action-button--list-view-only');
    });

    if (quickBuy === 'list_grid') {
      card.querySelectorAll('.product-item__action-button:not([data-secondary-action])').forEach(button => {
        button.classList.remove('product-item__action-button--list-view-only');
      });
    }
    if (quickBuy === 'list_grid' || quickView === 'list_grid') {
      card.querySelector('.product-item__action-list')?.classList.remove('product-item__action-list--list-view-only');
    }
  }

  // グリッド/リストのクラスを、選択中の表示に合わせる（theme.js の _changeLayout と同じ付け方）
  _applyLayout(root) {
    const section = this._section();
    if (!section) return;

    const mode = section.querySelector('.collection__layout-button.is-selected')?.dataset.layoutMode || 'grid';
    let gridClasses = '';
    try { gridClasses = JSON.parse(section.dataset.sectionSettings).gridClasses || ''; } catch {}

    root.querySelectorAll('.product-item[data-rewind-handle]').forEach(item => {
      item.className = mode === 'grid'
        ? `product-item product-item--vertical ${gridClasses}`
        : 'product-item product-item--list';
    });
  }

  _renderCounts(total, start, shown) {
    const section = this._section();
    const strings = this.settings.strings || {};
    const form    = total === 0 ? 'zero' : total === 1 ? 'one' : 'other';

    for (const [selector, name] of Object.entries(REWIND_COUNT_TARGETS)) {
      const template = strings[name]?.[form];
      if (template == null) continue;

      // 文言は Liquid 側で仮の数値を入れて作ってある
      const text = template
        .replace('111111', start + 1)
        .replace('222222', start + shown)
        .replace('333333', total);
      section.querySelectorAll(selector).forEach(el => { el.textContent = text; });
    }
  }

  // スクリーンリーダーに結果を伝える（main-collection.liquid の data-rewind-status）
  _announce(message) {
    const status = document.querySelector('[data-rewind-status]');
    if (status && status.textContent !== message) status.textContent = message;
  }

  // スライダーの条件を、一覧側にも出す。
  //   PC: サイドバー上部の適用中の一覧 / スマホ: 一覧の上のチップ /「フィルター (N)」とドロワーの見出しの件数
  // どれもテーマが描き直すたびに消えるので、applyFilter のたびに作り直す
  _renderIndicators() {
    const section = this._section();
    if (!section) return;

    const kept = section.querySelector('.rewind-spec-recap .collection__mobile-active-filters-results')?.textContent ?? '';
    section.querySelectorAll('.rewind-spec-chip, .rewind-spec-clear, .rewind-spec-recap').forEach(el => el.remove());

    const touched     = this._touchedFields();
    const drawerCount = section.querySelector('[data-rewind-drawer-count]');
    const serverCount = parseInt(drawerCount?.dataset.rewindDrawerCount, 10) || 0; // 形状などの絞り込みの数
    const total       = serverCount + touched.length;

    const toolbarCount = section.querySelector('[data-rewind-filter-count]');
    if (toolbarCount) {
      toolbarCount.textContent = total > 0 ? `\u00a0(${total})` : '';
      toolbarCount.parentNode.querySelector('.collection__filter-icon')?.classList.toggle('collection__filter-icon--active', total > 0);
    }
    if (drawerCount) {
      drawerCount.textContent = `(${total})`;
      drawerCount.style.display = total > 0 ? '' : 'none';
    }
    const drawerClear = section.querySelector('[data-rewind-drawer-clear]');
    if (drawerClear) drawerClear.style.display = total > 0 ? '' : 'none';

    if (touched.length === 0) return;

    const icon     = this.settings.icons?.close ?? '';
    const clearAll = this._escape(this.settings.strings?.clearAll ?? '');
    const chip = (f, itemClass, crossClass) =>
      `<button type="button" class="${itemClass} rewind-spec-chip" data-action="reset-rewind-slider" data-field="${f.key}">` +
      `<span class="${crossClass}">${icon}</span>${this._escape(`${f.label} ${f.min}${this.text.rangeSep}${f.max} ${f.unit}`)}</button>`;

    // PC。形状などの絞り込みがあればテーマの「すべて消去」が出ている（押すとスライダーも戻す。下の click を参照）
    const desktop = section.querySelector('#desktop-filters-form .collection__active-filters');
    if (desktop) {
      const html  = touched.map(f => chip(f, 'collection__active-filter-item', 'collection__active-filter-cross')).join('');
      const clear = desktop.querySelector('.collection__clear-filter');
      if (clear) clear.insertAdjacentHTML('beforebegin', html);
      else desktop.insertAdjacentHTML('beforeend', html +
        `<button type="button" class="collection__clear-filter button button--small button--transparent button--full rewind-spec-clear" data-action="reset-rewind-sliders">${clearAll}</button>`);
    }

    // スマホ。テーマのチップ欄は、形状などの絞り込みがあるときしか出ないので、無ければ作る
    const dynamic = section.querySelector('.collection__dynamic-part');
    let chips = dynamic?.querySelector('.collection__mobile-active-filters');
    if (!chips) {
      const toolbar = dynamic?.querySelector('.collection__toolbar');
      if (!toolbar) return;
      toolbar.insertAdjacentHTML('afterend',
        '<div class="card__section collection__mobile-filters-recap hidden-lap-and-up rewind-spec-recap">' +
        `<div class="collection__mobile-active-filters"></div><span class="collection__mobile-active-filters-results">${this._escape(kept)}</span></div>`);
      chips = dynamic.querySelector('.rewind-spec-recap .collection__mobile-active-filters');
    }

    const html  = touched.map(f => chip(f, 'collection__mobile-active-filter-item', 'collection__mobile-active-filter-cross')).join('');
    const clear = chips.querySelector('.collection__mobile-active-clear');
    if (clear) clear.insertAdjacentHTML('beforebegin', html);
    else if (total > 1) {
      // テーマと同じく、条件が2つ以上のときだけ「すべて消去」を出す
      const sortBy = new URLSearchParams(location.search).get('sort_by');
      const url    = location.pathname + (sortBy ? `?sort_by=${encodeURIComponent(sortBy)}` : '');
      const action = serverCount > 0 ? `data-action="clear-filters" data-url="${this._escape(url)}"` : 'data-action="reset-rewind-sliders"';
      chips.insertAdjacentHTML('beforeend', html + `<button type="button" class="collection__mobile-active-clear rewind-spec-clear" ${action}>${clearAll}</button>`);
    } else {
      chips.insertAdjacentHTML('beforeend', html);
    }
  }

  // スマホのドロワーは一覧を覆うので、下部のボタンに結果の件数を出す
  _updateDrawerButton() {
    const button = document.querySelector('[data-rewind-results-button]');
    if (!button) return;

    if (button.dataset.rewindLabel == null) button.dataset.rewindLabel = button.textContent;
    button.textContent = this._pendingLabel ? this.text.filtering
      : ((this._isTouched() || this._soldOutOnly()) && this._lastTotal != null) ? this.text.showResults(this._lastTotal)
      : button.dataset.rewindLabel;
  }

  // ページ送り・解除でフォーカスしていた要素が消えたときの行き先
  _focusList() {
    const list = this._section()?.querySelector('.product-list--collection');
    if (!list) return;
    list.setAttribute('tabindex', '-1');
    list.focus({ preventScroll: true });
  }

  _renderPagination(pages) {
    const { pagination } = this._spec;
    pagination.setAttribute('role', 'navigation');
    pagination.setAttribute('aria-label', this.text.pagination);
    pagination.hidden = pages <= 1;
    if (pages <= 1) { pagination.innerHTML = ''; return; }

    const strings = this.settings.strings || {};
    const icons   = this.settings.icons || {};
    const current = this.page;
    const text    = value => this._escape(value ?? '');

    // テーマの .pagination [data-page] に拾われないよう、data-rewind-page を使う
    let html = '<div class="pagination__inner">';

    if (current > 1) {
      html += `<a class="pagination__prev link" rel="prev" title="${text(strings.previousPage)}" data-rewind-page="${current - 1}" href="${this._pageUrl(current - 1)}">${icons.previous ?? ''} ${text(strings.previousPage)}</a>`;
    }

    html += '<div class="pagination__nav">';
    let last = 0;
    for (let page = 1; page <= pages; page++) {
      if (page !== 1 && page !== pages && Math.abs(page - current) > 2) continue;
      if (page - last > 1) html += '<span class="pagination__nav-item">&hellip;</span>';
      const goTo = text((strings.goToPage ?? '').replace('111111', page));
      html += page === current
        ? `<span class="pagination__nav-item is-active" aria-current="page">${page}</span>`
        : `<a href="${this._pageUrl(page)}" data-rewind-page="${page}" class="pagination__nav-item link" title="${goTo}" aria-label="${goTo}">${page}</a>`;
      last = page;
    }
    html += '</div>';

    html += `<span class="pagination__page-count">${text((strings.pageCount ?? '').replace('111111', current).replace('222222', pages))}</span>`;

    if (current < pages) {
      html += `<a class="pagination__next link" rel="next" title="${text(strings.nextPage)}" data-rewind-page="${current + 1}" href="${this._pageUrl(current + 1)}">${text(strings.nextPage)} ${icons.next ?? ''}</a>`;
    }

    pagination.innerHTML = html + '</div>';
  }

  goToPage(page) {
    this.page = page;
    // ページ送りは履歴に残す（テーマのページ送りと同じ。「戻る」で前のページに戻れる）
    this._updateUrl(/* push= */ true);
    this.applyFilter();
    this._focusList();

    // 一覧の先頭へ（固定ヘッダーの高さぶんを空ける）
    const target = this._section()?.querySelector('.collection__dynamic-part');
    if (!target) return;
    const styles = getComputedStyle(document.documentElement);
    const header = (parseInt(styles.getPropertyValue('--header-is-sticky'), 10) || 0) * (parseInt(styles.getPropertyValue('--header-height'), 10) || 0);
    const offset = target.getBoundingClientRect().top - 25 - header;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (offset < 0) window.scrollBy({ top: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  _setPending(pending) {
    this._section()?.classList.toggle('rewind-spec-pending', pending);

    if (pending !== this._pending) {
      this._pending = pending;
      this._pendingLabel = false;
      clearTimeout(this._pendingTimer);
      if (pending) {
        // 300ms 以内に終わるなら、文言を切り替えない（DESIGN.md §9）
        this._pendingTimer = setTimeout(() => {
          this._pendingLabel = true;
          this._updateDrawerButton();
        }, 300);
      }
    }
    this._updateDrawerButton();
  }

  _escape(value) {
    const el = document.createElement('span');
    el.textContent = String(value);
    return el.innerHTML.replace(/"/g, '&quot;');
  }

  // ─────────────────────────────────────────────
  // URLにフィルタ状態を書き込む
  // ─────────────────────────────────────────────
  _params(page) {
    const params = new URLSearchParams(location.search);

    for (const [key, { min, max }] of Object.entries(this.sliderRanges)) {
      const dataRange = this.sliderDataRanges[key];
      const isTouched = dataRange && (min > dataRange.min || max < dataRange.max);
      if (isTouched) {
        params.set(`${key}_min`, min);
        params.set(`${key}_max`, max);
      } else {
        params.delete(`${key}_min`);
        params.delete(`${key}_max`);
      }
    }

    if (page > 1 && this._isActive()) params.set('spec_page', page);
    else params.delete('spec_page');

    return params;
  }

  _pageUrl(page) {
    const qs = this._params(page).toString();
    return this._escape(location.pathname + (qs ? '?' + qs : ''));
  }

  _updateUrl(push = false) {
    const qs = this._params(this.page).toString();
    const newUrl = location.pathname + (qs ? '?' + qs : '') + location.hash;
    if (push) history.pushState(history.state, '', newUrl);
    else history.replaceState(history.state, '', newUrl);
  }
}

// ─────────────────────────────────────────────
// 起動・管理
// ─────────────────────────────────────────────
let _rewindFilter = null;

function initRewindFilter() {
  const app = document.querySelector('.rewind-filter-app');
  if (!app) return;

  const collectionHandle = app.dataset.collection;
  const storefrontToken  = app.dataset.token;
  const storeDomain      = app.dataset.domain || window.Shopify?.shop || location.hostname;
  const locale           = app.dataset.locale || document.documentElement.lang || 'ja';

  if (!collectionHandle || !storefrontToken) {
    console.warn('[RewindFilter] data-collection または data-token が設定されていません');
    return;
  }

  let settings = {};
  try {
    settings = JSON.parse(document.querySelector('[data-rewind-filter-config]').textContent);
  } catch {
    console.warn('[RewindFilter] data-rewind-filter-config を読めません');
  }

  _rewindFilter = new RewindFilter({ collectionHandle, storefrontToken, storeDomain, locale, settings });
  _rewindFilter.init();
}

// 並び順を選ぶと、テーマがツールバーごと描き直すので、フォーカスしていた項目が消える（フォーカスが body に落ちる）。
// 描き直しの後、並び順のボタンへ戻す
let _rewindSortPicked = false;

document.addEventListener('click', function(e) {
  if (e.target.closest('#sort-by-selector [data-action="select-value"]')) _rewindSortPicked = true;
}, true);

function refreshRewindFilter() {
  if (_rewindSortPicked) {
    _rewindSortPicked = false;
    if (!document.activeElement || document.activeElement === document.body) {
      document.querySelector('[data-action="open-value-picker"][aria-controls="sort-by-selector"]')?.focus({ preventScroll: true });
    }
  }

  if (_rewindFilter) {
    _rewindFilter.refresh();
  } else {
    initRewindFilter();
  }
}

document.addEventListener('DOMContentLoaded', initRewindFilter);
document.addEventListener('theme:loading:end', refreshRewindFilter);

// 戻る/進む: theme.js が一覧を取り直して theme:loading:end を出す。そのときだけ URL の範囲とページを読む
window.addEventListener('popstate', function() {
  if (_rewindFilter) _rewindFilter._fromHistory = true;
});

// スペック表示のページ送り・解除ボタン
document.addEventListener('click', function(e) {
  if (!_rewindFilter) return;

  const pageLink = e.target.closest('[data-rewind-page]');
  if (pageLink) {
    // 新しいタブで開く操作はそのまま通す（URL から同じ状態を復元できる）
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    _rewindFilter.goToPage(parseInt(pageLink.dataset.rewindPage, 10));
    return;
  }

  // 押したチップやボタンは描き直しで消えるので、消えたらフォーカスを一覧へ移す
  const chip = e.target.closest('[data-action="reset-rewind-slider"]');
  if (chip || e.target.closest('[data-action="reset-rewind-sliders"]')) {
    if (chip) _rewindFilter.resetSlider(chip.dataset.field);
    else _rewindFilter.resetSliders();

    _rewindFilter.applyFilter().then(() => {
      if (!document.activeElement || document.activeElement === document.body) _rewindFilter._focusList();
    });
  }
});

// テーマの「すべて消去」（形状などの絞り込みを全部外す）が押されたら、スライダーも初期位置に戻す。
// 個別のチップ（1条件だけ外す）は対象外。テーマの処理より先に動くよう capture で受ける
document.addEventListener('click', function(e) {
  if (!_rewindFilter) return;

  const clear = e.target.closest('[data-action="clear-filters"]');
  if (clear && !clear.matches('.collection__active-filter-item, .collection__mobile-active-filter-item')) {
    _rewindFilter.clearRanges();
  }
}, true);

// 並び順のメニューは、重量・直径・幅の項目が加わって長くなった（PC で約540px）。
// ノートPCの画面では下の項目（＝追加した項目）が画面の外に出るので、はみ出す分はメニューの中でスクロールさせる
// （実測: 1280x720 の /collections/all で、一覧の高さ 539px → 271px）。
// スマホは、テーマが一覧の高さを止めている（中でスクロールする）ので、高さは触らない
document.addEventListener('click', function(e) {
  if (!e.target.closest('[data-action="open-value-picker"][aria-controls="sort-by-selector"]')) return;

  const list = document.querySelector('#sort-by-selector .value-picker__choice-list');
  if (!list) return;

  list.style.maxHeight = '';
  if (window.matchMedia('(min-width: 641px)').matches) {
    const room = window.innerHeight - list.getBoundingClientRect().top - 20;
    if (list.scrollHeight > room) list.style.maxHeight = `${Math.max(room, 150)}px`;
  }

  // 選択中の項目が、スクロールしないと見えない位置にあることがある（下のほうの項目を選んでいるとき）。真ん中あたりへ出す
  const selected = list.querySelector('.is-selected');
  if (selected) {
    list.scrollTop += selected.getBoundingClientRect().top - list.getBoundingClientRect().top
      - (list.clientHeight - selected.offsetHeight) / 2;
  }
});
